import type { Content } from "@features/api/openData";
import { NewsRoutePaths } from "@config/router";
import { buildLocale } from "@utils/buildLocale";
import type {
  ThemeItemWithParent,
  ThemeRowWithRanking,
} from "@utils/newsRanking";

/**
 * 热门新闻列表项：在 {@link Content} 上附加与 `theme_ids` 对齐的 ranking theme 行（含已解析的 `parentTheme`）。
 */
export type ContentWithThemeRanking = Content & {
  /** 与 `theme_ids` 一一对应；ranking 中无对应 id 或未加载时为 null */
  themeRowsWithRanking?: (ThemeItemWithParent | null)[];
};

/**
 * 按 `theme_ids` 从 ranking themes 列表解析每条 Content 对应的行，并挂上 `parentTheme`（与 {@link ThemeItemWithParent} 一致）。
 */
export function attachThemeRankingToContents(
  items: Content[],
  rankingRows: ThemeRowWithRanking[],
): ContentWithThemeRanking[] {
  if (items.length === 0) {
    return [];
  }

  const byId = new Map<string, ThemeRowWithRanking>();
  for (const row of rankingRows) {
    const id = row.id;
    if (id) {
      byId.set(id, row);
    }
  }

  return items.map((item) => {
    const ids = item.theme_ids;
    if (!ids?.length) {
      return { ...item };
    }

    const themeRowsWithRanking = ids.map((tid): ThemeItemWithParent | null => {
      if (!tid) {
        return null;
      }
      const row = byId.get(tid);
      if (!row) {
        return null;
      }
      const parentId = row.parent_theme_id;
      const parentRow = parentId != null ? byId.get(parentId) : undefined;
      return {
        ...row,
        parentTheme: parentRow != null ? parentRow : null,
      };
    });

    return { ...item, themeRowsWithRanking };
  });
}

/**
 * 由已带 `parentTheme` 的子主题行解析 `/news/{channel}/{subTagSlug}`。
 * 父级须为根主题（`parent_theme_id` 为空）。
 */
export function resolveNewsSubTagPathFromThemeRow(
  themeRow: ThemeItemWithParent | null | undefined,
): { channel: string; subTagSlug: string } | null {
  if (!themeRow?.slug) {
    return null;
  }

  const parent = themeRow.parentTheme as ThemeRowWithRanking | null | undefined;
  const channel = parent?.slug;
  if (!channel || parent.parent_theme_id != null) {
    return null;
  }

  return { channel, subTagSlug: themeRow.slug };
}

/**
 * 由 `useHotNews` 返回的条目（`themeRowsWithRanking` 已含 `parentTheme`）生成主题子标签页路径（含 locale 前缀）。
 */
export function buildNewsThemeSubTagHref(
  item: ContentWithThemeRanking,
  locale: string,
): string | undefined {
  const themeRow =
    item.themeRowsWithRanking?.find((r) => r != null) ?? undefined;
  const params = resolveNewsSubTagPathFromThemeRow(themeRow);
  if (!params) {
    return undefined;
  }
  return NewsRoutePaths.subTag(params.channel, params.subTagSlug, {
    localePathPrefix: buildLocale(locale),
  });
}
